import { useState, useContext } from 'react';
import Separator from './Separator';
import { Button } from '@mui/material';
import { AddCircle as AddIcon } from '@mui/icons-material';
import Prompt from '@/components/Prompt';
import Dialog from '@/components/Dialog';
import { FormattedMessage } from 'react-intl';
import ModelsContext from '../../ModelsContext';
import useApi from '@/lib/api/useApi';
import { type EditorSectionWithoutRelations } from '@/lib/types';
import SelectedModelIdContext from '../../SelectedModelIdContext';
import EditorIsReadonlyContext from '../../EditorIsReadonlyContext';

type AddModelSectionProps = {
  highestSectionPosition: number;
};

const AddModelSection = ({ highestSectionPosition }: AddModelSectionProps) => {
  const api = useApi();

  const [, setModels] = useContext(ModelsContext);
  const selectedModelId = useContext(SelectedModelIdContext);

  const [addPromptOpen, setAddPromptOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveNewModelSection = async (name: string): Promise<void> => {
    setAddPromptOpen(false);
    setSaving(true);

    const sectionData = {
      name,
      position: highestSectionPosition + 1,
    };

    const section = await api.post<EditorSectionWithoutRelations>(
      `editor/challenge-model/sections?modelId=${selectedModelId}`,
      sectionData,
    );

    setModels(models => {
      models[selectedModelId].sections[section.id] = {
        ...section,
        groups: [],
        fields: [],
      };
    });

    setSaving(false);
  };

  const editorIsReadOnly = useContext(EditorIsReadonlyContext);

  return (
    <>
      <Separator />
      <Button
        color="secondary"
        onClick={() => setAddPromptOpen(true)}
        startIcon={<AddIcon />}
        disabled={saving || editorIsReadOnly}
        fullWidth
      >
        <FormattedMessage id="editor.model_editor.add_section.button" />
      </Button>
      <Prompt
        open={addPromptOpen}
        onCancel={() => setAddPromptOpen(false)}
        onOk={saveNewModelSection}
        OkButton={Dialog.Actions.CreateButton}
        emptyError="prompt.errors.name_empty"
      >
        <Dialog.Title>
          <FormattedMessage id="editor.model_editor.add_section.title" />
        </Dialog.Title>
        <Prompt.TextField
          label={
            <FormattedMessage id="editor.model_editor.add_section.label" />
          }
        />
      </Prompt>
    </>
  );
};

export default AddModelSection;
